/**
 * Shared styles for common components
 * Includes all animations and hover effects used across pages
 */

export const sharedStyles = `
  /* Policy Card Animations */
  .policy-card {
    transition: all 0.3s ease;
  }

  .policy-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  .policy-icon {
    transition: transform 0.4s ease;
  }

  .policy-card:hover .policy-icon {
    transform: scale(1.1);
  }

  /* Accordion Animations */
  .accordion-section {
    transition: all 0.3s ease;
  }

  .accordion-section:hover {
    background-color: #f8f9fa;
  }

  .accordion-header {
    cursor: pointer;
    user-select: none;
  }

  .accordion-icon {
    transition: transform 0.3s ease;
  }

  .accordion-content {
    overflow: hidden;
    transition: max-height 0.3s ease;
  }

  /* Search Bar Styles */
  .search-input-wrapper {
    position: relative;
  }

  .search-input {
    width: 100%;
    padding: 16px 24px 16px 56px;
    border: 2px solid #e5e7eb;
    border-radius: 50px;
    font-size: 16px;
    transition: all 0.3s ease;
  }

  .search-input:focus {
    outline: none;
    border-color: var(--main-600, #2c3e50);
    box-shadow: 0 0 0 4px rgba(44, 62, 80, 0.1);
  }

  .search-icon {
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    font-size: 20px;
  }

  /* Category Filter Buttons */
  .category-btn {
    transition: all 0.3s ease;
  }

  .category-btn:hover {
    transform: translateY(-2px);
  }

  /* Quick Navigation Sidebar */
  .quick-nav {
    position: sticky;
    top: 100px;
    background: var(--color-one, #f8f9fa);
    border-radius: 12px;
    padding: 24px;
    border: 1px solid #e5e7eb;
    max-height: calc(100vh - 150px);
    overflow-y: auto;
  }

  .quick-nav h4 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #1a202c;
  }

  .quick-nav-item {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    margin-bottom: 8px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: var(--neutral-600, #4a5568);
    font-size: 14px;
    border-left: 3px solid transparent;
  }

  .quick-nav-item:hover {
    background: white;
    color: var(--main-600, #2c3e50);
    border-left-color: var(--main-600, #2c3e50);
    transform: translateX(4px);
  }

  .quick-nav-item.active {
    background: white;
    color: var(--main-600, #2c3e50);
    border-left-color: var(--main-600, #2c3e50);
    font-weight: 600;
  }

  /* Quick Navigation Link */
  .quick-nav-link {
    transition: all 0.2s ease;
    padding: 8px 12px;
    border-radius: 6px;
  }

  .quick-nav-link:hover {
    background-color: rgba(44, 62, 80, 0.05);
    transform: translateX(4px);
  }

  /* Timeline Styles */
  .timeline-item {
    position: relative;
    padding-left: 40px;
  }

  .timeline-item::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 32px;
    bottom: -16px;
    width: 2px;
    background: linear-gradient(180deg, #2c3e50 0%, transparent 100%);
  }

  .timeline-item:last-child::before {
    display: none;
  }

  .timeline-dot {
    position: absolute;
    left: 0;
    top: 8px;
    width: 18px;
    height: 18px;
    background: var(--main-600, #2c3e50);
    border-radius: 50%;
    border: 3px solid #fff;
    box-shadow: 0 0 0 4px rgba(44, 62, 80, 0.1);
  }

  /* Step Circle Animation */
  .step-circle {
    transition: all 0.3s ease;
  }

  .step-circle:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(44, 62, 80, 0.2);
  }

  /* Privacy Section Cards */
  .privacy-section {
    background: var(--color-one, #f8f9fa);
    border-radius: 12px;
    padding: 40px;
    margin-bottom: 24px;
    border: 1px solid #e5e7eb;
    transition: all 0.4s ease;
  }

  .privacy-section:hover {
    border-color: var(--main-600, #2c3e50);
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(44, 62, 80, 0.1);
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 28px;
    padding-bottom: 20px;
    border-bottom: 2px solid #e2e8f0;
  }

  .section-icon {
    width: 64px;
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--main-600, #2c3e50);
    border-radius: 50%;
    font-size: 28px;
    transition: all 0.3s ease;
  }

  .privacy-section:hover .section-icon {
    transform: rotate(360deg);
  }

  .section-title {
    font-size: 24px;
    font-weight: 700;
    color: #1a202c;
    margin: 0;
  }

  .content-item {
    margin-bottom: 24px;
    padding-left: 20px;
    border-left: 3px solid #e2e8f0;
    transition: all 0.3s ease;
  }

  .content-item:last-child {
    margin-bottom: 0;
  }

  .content-item:hover {
    border-left-color: var(--main-600, #2c3e50);
    padding-left: 28px;
  }

  .content-subtitle {
    font-size: 16px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .content-subtitle::before {
    content: '▸';
    color: var(--main-600, #2c3e50);
    font-weight: bold;
  }

  .content-text {
    color: var(--neutral-600, #4a5568);
    line-height: 1.8;
    font-size: 15px;
  }

  /* FAQ Item Styles */
  .faq-item {
    transition: all 0.3s ease;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 16px;
    border: 1px solid #e5e7eb;
    background: white;
  }

  .faq-item:hover {
    border-color: var(--main-600, #2c3e50);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  .faq-question {
    cursor: pointer;
    user-select: none;
  }

  .faq-icon {
    transition: transform 0.3s ease;
  }

  .faq-answer {
    overflow: hidden;
    transition: max-height 0.3s ease;
  }

  /* Shipping Option Card */
  .shipping-option-card {
    border: 1px solid #e5e7eb;
    transition: all 0.3s ease;
  }

  .shipping-option-card:hover {
    border-color: var(--main-600, #2c3e50);
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  /* Quick Help Card */
  .quick-help-card {
    text-decoration: none;
    display: block;
    height: 100%;
  }

  .quick-help-card-inner {
    height: 100%;
    transition: all 0.3s ease;
  }

  .quick-help-card:hover .quick-help-card-inner {
    transform: translateY(-8px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }

  /* Tip Item Hover Effect */
  .tip-item {
    transition: all 0.2s ease;
    padding: 12px;
    border-radius: 8px;
  }

  .tip-item:hover {
    background-color: white;
  }

  /* Delivery Table Styles */
  .delivery-table {
    border-collapse: separate;
    border-spacing: 0;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
  }

  .delivery-table thead {
    background: var(--color-one, #f8f9fa);
  }

  .delivery-table th,
  .delivery-table td {
    padding: 16px;
    border-bottom: 1px solid #e5e7eb;
  }

  .delivery-table tbody tr:last-child td {
    border-bottom: none;
  }

  .delivery-table tbody tr {
    transition: background-color 0.2s ease;
  }

  .delivery-table tbody tr:hover {
    background-color: #f8f9fa;
  }

  /* About Page Specific Animations */
  .about-thumb {
    overflow: hidden;
    border-radius: 0.375rem;
  }

  .about-thumb img {
    transition: transform 0.5s ease-in-out;
    display: block;
    width: 100%;
  }

  .about-thumb:hover img {
    transform: scale(1.1);
  }

  .mission-vision-card {
    transition: all 0.3s ease;
  }

  .mission-vision-icon {
    transition: transform 0.6s ease-in-out;
  }

  .mission-vision-card:hover .mission-vision-icon {
    transform: rotate(360deg);
  }

  .value-icon-wrapper {
    transition: all 0.3s ease;
  }

  .value-card:hover .value-icon-wrapper {
    animation: bounceIcon 0.6s ease;
  }

  @keyframes bounceIcon {
    0%, 100% {
      transform: translateY(0) scale(1);
    }
    25% {
      transform: translateY(-10px) scale(1.1);
    }
    50% {
      transform: translateY(-5px) scale(1.05);
    }
    75% {
      transform: translateY(-8px) scale(1.08);
    }
  }

  .team-member-image {
    border-radius: 50%;
    background-color: #f8f9fa;
    border: 4px solid #e5e7eb;
    padding: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    display: block;
  }

  .team-member-image:hover {
    border-color: var(--main-600, #2c3e50);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    transform: scale(1.05);
  }

  /* Scrollbar Styles */
  .quick-nav::-webkit-scrollbar {
    width: 6px;
  }

  .quick-nav::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  .quick-nav::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
  }

  .quick-nav::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }

  /* Responsive Styles */
  @media (max-width: 991px) {
    .quick-nav {
      position: relative;
      top: 0;
      margin-bottom: 32px;
      max-height: none;
    }

    .privacy-section {
      padding: 28px;
    }

    .section-title {
      font-size: 20px;
    }
  }

  @media (max-width: 768px) {
    .section-header {
      flex-direction: column;
      align-items: flex-start;
    }

    .content-item {
      padding-left: 16px;
    }
  }
`;

export default sharedStyles;
